/**
 * Exit popup v2: who is leaving, and what we say to them.
 *
 * Layer 1 (context) picks the message and where the button goes. Layer 2 (engagement) picks the supporting line.
 * Owner rules (2026-10-03): no call or phone CTA anywhere, never "call / book / session / text" or a length of time,
 * every button goes to the demo form or its pricing version (?source=pricing), value and benefit language only, no numbers
 * except the canonical social proof and the visitor's own real session counts. COPY IS A DRAFT until Andrew approves it.
 */

export type Ctx = 'pricing' | 'leadtype' | 'leak' | 'aiguide' | 'guide' | 'ai' | 'returning' | 'default';
export type Level = 'light' | 'browsing' | 'engaged';

export interface Visit {
  path: string;
  pages: string[];      // unique paths seen this visit, in order
  seconds: number;      // time since the visit started (whole site, not this page)
  visits: number;       // 1 = first visit, 2+ = returning
  ai: string | null;    // 'ChatGPT', 'Perplexity'... when the visit came from an AI assistant
  leakStarted: boolean; // used the Leak Finder this visit
  leakHref: string | null; // the Leak Finder's own CTA link, picks included
  leakCount: number;
  h1: string | null;
}

export interface Msg {
  id: string;           // tracking id, one per context x level
  ctx: Ctx;
  tag: string;
  headline: string;
  line: string;
  rows: [string, string][]; // the "Your LeadPops plan" preview card
  form?: FormState;     // set on the demo form: the popup shows progress and locked rewards instead of the plan card
  locked?: [string, string][]; // rewards that unlock at the finish (form popup)
  button: string;
  note: string;
  secondary: string;    // text link that closes the popup
  href: string;
}

// Popup never shows on the form, its pricing version, thank-you or checkout pages, or the privacy pages (owner 2026-10-14).
const NEVER = ['/mortgage-lead-generation-demo', '/demo-form-next', '/pricing', '/schedule-demo',
  '/thank-you', '/checkout', '/payment', '/confirmation', '/admin', '/report',
  '/privacy-policy', '/privacy-choices', '/ccpa-compliance', '/we-do-not-sell-personal-information', '/disclosure-and-deletion-requests'];
export const blockedPath = (p: string) => NEVER.some((n) => p === n || p.startsWith(n + '/') || p.startsWith(n + '-'));

const LEAD_TYPES: Record<string, string> = {
  '/non-qm-mortgage-leads': 'non-QM', '/va-mortgage-leads': 'VA', '/fha-mortgage-leads': 'FHA', '/jumbo-mortgage-leads': 'jumbo',
  '/reverse-mortgage-leads': 'reverse mortgage', '/refinance-mortgage-leads': 'refinance', '/purchase-mortgage-leads': 'purchase',
  '/conventional-mortgage-leads': 'conventional',
};
const PRICING_PAGES = ['/mortgage-marketing-pricing'];
// The AI-visibility guide gets its own message: the generic guide promise ("everything in this guide, done for you") would promise
// review and directory profile work LeadPops does not do (owner 2026-10-15). This one offers only the lead system the site sells.
const AI_GUIDE = '/guide/how-loan-officers-get-cited-by-ai';

const AI_HOSTS: [RegExp, string][] = [
  [/chatgpt\.com|chat\.openai\.com/, 'ChatGPT'], [/perplexity\.ai/, 'Perplexity'], [/copilot\.microsoft\.com/, 'Copilot'],
  [/gemini\.google\.com/, 'Gemini'], [/claude\.ai/, 'Claude'],
];
export function aiSource(referrer: string, search: string): string | null {
  const utm = new URLSearchParams(search).get('utm_source') || '';
  for (const [re, name] of AI_HOSTS) if (re.test(referrer) || re.test(utm)) return name;
  return null;
}

export function level(v: Visit): Level {
  if (v.leakStarted || v.seconds >= 300 || v.pages.length >= 4) return 'engaged';
  if (v.seconds < 60 && v.pages.length <= 1) return 'light';
  return 'browsing';
}

export function context(v: Visit): Ctx {
  if (v.leakStarted && v.path === '/mortgage-marketing') return 'leak';
  if (v.pages.some((p) => PRICING_PAGES.includes(p))) return 'pricing';
  if (LEAD_TYPES[v.path]) return 'leadtype';
  if (v.path === AI_GUIDE) return 'aiguide';
  if (v.path.startsWith('/guide/') || v.path.startsWith('/blog/')) return 'guide';
  if (v.ai) return 'ai';
  if (v.visits > 1) return 'returning';
  return 'default';
}

const DEMO = '/mortgage-lead-generation-demo';
const withId = (href: string, id: string) => `${href}${href.includes('?') ? '&' : '?'}xp=${id}`;
const PRICE_ROW: [string, string] = ['Pricing', 'Fits your budget'];
const MINE_ROW: [string, string] = ['Every lead', 'Yours alone'];

export function message(ctx: Ctx, lvl: Exclude<Level, 'light'>, v: Visit): Msg {
  const e = lvl === 'engaged';
  const id = `${ctx}-${e ? 'e' : 'b'}`;
  const base = { id, ctx, secondary: 'Keep browsing', note: 'Free and built for you. No obligation.' };
  switch (ctx) {
    case 'pricing':
      return { ...base, tag: 'You looked at pricing', headline: 'Get pricing built for your market',
        line: e ? "You've done the homework. Get the real number for your market, with the plan behind it."
          : 'Tell us where you lend and what you write. You get a plan with pricing that fits your goals and your budget.',
        rows: [['Your market', 'Mapped'], ['Loan products', 'Your mix'], ['Monthly budget', 'Your call'], ['Your pricing', 'Built for you']],
        button: 'Show me my pricing', href: withId(`${DEMO}?source=pricing`, id) };
    case 'leadtype': {
      const t = LEAD_TYPES[v.path] || 'mortgage';
      return { ...base, id: `${id}-${v.path.slice(1).replace(/-mortgage-leads$/, '')}`, tag: `${cap(t)} leads`,
        headline: `Your own ${t} leads, in your market`,
        line: e ? `You've spent real time on ${t}. See exactly how your own ${t} lead flow would work.`
          : `See the system we'd build to bring ${t} borrowers straight to you. Every lead is yours alone.`,
        rows: [['Your market', 'Mapped'], ['Loan product', cap(t)], MINE_ROW, PRICE_ROW],
        button: `Show me my ${t} plan`, note: 'Exclusive to you. Never shared with another lender.', href: withId(DEMO, id) };
    }
    case 'leak':
      return { ...base, tag: 'From your Leak Finder', headline: 'You found the leaks. Now get them fixed.',
        line: "Take your picks with you. We'll build your plan around the exact leaks you flagged.",
        rows: [['Leaks you flagged', v.leakCount ? String(v.leakCount) : 'Your picks'], ['A fix for each one', 'Mapped'], ['Your market', 'Mapped'], PRICE_ROW],
        button: 'Fix my leaks', note: 'Your Leak Finder picks come with you.',
        href: withId(v.leakHref || `${DEMO}?ref=leak-finder`, id) };
    case 'aiguide':
      return { ...base, tag: v.h1 ? `Reading: ${clip(v.h1, 44)}` : 'From the AI search guide', headline: 'Being found is step one. Leads are the goal.',
        line: e ? "You've read the research. Now see the lead system we'd build for your market, with pricing that fits your goals."
          : "See the system we'd build to bring borrowers in your market straight to you. Every lead is yours alone.",
        rows: [['Your market', 'Mapped'], ['Loan products', 'Your mix'], MINE_ROW, PRICE_ROW],
        button: 'Show me my plan', secondary: 'Back to the guide', href: withId(DEMO, id) };
    case 'guide':
      return { ...base, tag: v.h1 ? `Reading: ${clip(v.h1, 44)}` : 'From the guide', headline: "Like the strategy? We'll build it for you.",
        line: e ? "You've read this far, so you know it works. Get it running in your market, under your name."
          : 'Everything in this guide, set up and run in your market under your name.',
        rows: [['The strategy', 'From this guide'], ['Setup', 'Done for you'], ['Runs under', 'Your name'], PRICE_ROW],
        button: 'Show me my plan', note: 'Done for you. No obligation.', secondary: 'Back to the guide', href: withId(DEMO, id) };
    case 'ai': {
      const a = v.ai || 'AI';
      return { ...base, tag: `Sent here by ${a}`, headline: `${a} sent you here. Now see your version.`,
        line: 'It gave you the general answer. Your plan shows how this works in your market, with pricing to match.',
        rows: [['Your market', 'Mapped'], ['Loan products', 'Your mix'], MINE_ROW, PRICE_ROW],
        button: 'Show me my plan', href: withId(DEMO, id) };
    }
    case 'returning':
      return { ...base, tag: 'Welcome back', headline: 'Still thinking it over? See it built for you.',
        line: e ? 'You keep coming back for a reason. Get your plan and pricing, then decide.'
          : 'Get your plan and pricing for your market. Then decide with real answers.',
        rows: [['Your market', 'Mapped'], ['Loan products', 'Your mix'], MINE_ROW, PRICE_ROW],
        button: 'Show me my plan', href: withId(DEMO, id) };
    default:
      return { ...base, tag: 'Before you go', headline: 'See what your own lead system would look like',
        line: e ? "You've seen how it works. Now see it built for your market, with pricing that fits your goals."
          : 'A plan built for your market, with pricing that fits your goals. Every lead it brings in is yours alone.',
        rows: [['Your market', 'Mapped'], ['Loan products', 'Your mix'], MINE_ROW, PRICE_ROW],
        button: 'Show me my plan', href: withId(DEMO, id) };
  }
}

// Lab: a stand-in visit so every context can be previewed from any page.
export const LAB_SAMPLES: Record<Ctx, Partial<Visit>> = {
  pricing: { pages: ['/', '/mortgage-marketing-pricing'] },
  leadtype: { path: '/non-qm-mortgage-leads' },
  leak: { path: '/mortgage-marketing', leakStarted: true, leakCount: 3 },
  aiguide: { path: AI_GUIDE, h1: 'How Loan Officers Get Found and Cited in AI Search' },
  guide: { path: '/guide/mortgage-marketing-strategies', h1: 'Mortgage Marketing Strategies' },
  ai: { ai: 'ChatGPT' },
  returning: { visits: 2 },
  default: {},
};
export const CTX_LABELS: Record<Ctx, string> = {
  pricing: 'Looked at pricing', leadtype: 'Lead-type page (non-QM)', leak: 'Used the Leak Finder', aiguide: 'AI search guide reader', guide: 'Guide or blog reader',
  ai: 'Came from ChatGPT', returning: 'Returning visitor', default: 'Everyone else',
};

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }
function clip(s: string, n: number) { const t = s.trim(); return t.length > n ? t.slice(0, n - 1).trimEnd() + '…' : t; }

/* ── Demo form (game version) ───────────────────────────────────────────────────────────────────────────────────
 * One job: keep them on the form and get them to finish. Never sends them anywhere else. Every number shown is read
 * from the form's own HUD (progress %, level, questions left, XP), so the popup always matches what the form says.
 * rebel Ai carrot (14-day free trial + a free rebel site, free forever, unlocked when they book) is LAB-ONLY until the
 * unlock-at-booking flow exists; it must never show to real visitors before then.
 */
export const FORM_PATH = '/mortgage-lead-generation-demo';
/** the demo form and the pricing form (its public address is /pricing; /mortgage-marketing-pricing is a normal page) */
export const isFormPath = (p: string) => p.startsWith(FORM_PATH) || p === '/pricing' || p.startsWith('/pricing/');
export interface FormState { pct: number; level: string | null; levelName: string | null; left: number | null; xp: string | null;
  step: string | null; pricing: boolean }

export function readForm(): FormState | null {
  const bar = document.querySelector('.gm-xpbar');
  if (!bar) return null; // not the game form
  const q = new URLSearchParams(window.location.search);
  const left = document.querySelector('.gm-left')?.textContent?.match(/\d+/)?.[0];
  const xpm = document.querySelector('.gm-xp')?.textContent?.match(/([\d,]+)\s*XP/);
  const xp = xpm && Number(xpm[1].replace(/,/g, '')) > 0 ? xpm[0] : null;
  return { pct: Number(bar.getAttribute('aria-valuenow')) || 0, level: document.querySelector('.gm-lvl-n')?.textContent?.trim() || null,
    levelName: document.querySelector('.gm-lvl-t')?.textContent?.trim() || null, left: left ? Number(left) : null, xp,
    step: q.get('step'), pricing: q.get('source') === 'pricing' || window.location.pathname.startsWith('/pricing') };
}
// never interrupt the last moments
export const formDone = (f: FormState) => ['submitting', 'complete'].includes(f.step || '') || window.location.pathname.includes('/thank-you');

export function formMessage(f: FormState, carrot: boolean): Msg {
  const band = f.pct <= 0 || !f.step || f.step === 'welcome' ? 'start' : f.pct < 40 ? 'early' : f.pct < 75 ? 'mid' : f.level === 'BOSS LEVEL' ? 'boss' : 'late';
  // What we can honestly promise to EVERYONE who finishes (owner, 2026-10-04): their recommended next step. Not a call
  // (the sales route only), not a plan or pricing (those come on the call), not rebel Ai (unlocks at booking, sales
  // route only, not built yet). Most leads are routed away from the sales call, so none of those can be the promise.
  const headline = {
    start: 'Find out your best next step',
    early: 'Your recommendation is taking shape',
    mid: `You're ${f.pct}% of the way to your recommendation`,
    late: 'Almost there. Your recommendation is nearly ready.',
    boss: 'Last step. Your recommendation is next.',
  }[band];
  const line = {
    start: 'A few answers about your business and we\'ll show you how we can help you grow.',
    early: 'Every answer makes it more specific to your business.',
    mid: 'Finish up and we\'ll show you the right next step for your business.',
    late: 'A few more answers and it\'s built around you.',
    boss: 'Finish and you\'ll see it.',
  }[band];
  const tag = band === 'start' ? 'Ready when you are' : [f.level, f.levelName].filter(Boolean).join(' · ') || 'Your progress';
  const locked: [string, string][] = [['Your recommended next step', 'Built from your answers']];
  // rebel Ai rows are LAB-ONLY (and off by default): unlock-at-booking does not exist yet and only the sales route books.
  if (carrot) locked.push(['rebel Ai, 14-day free trial', 'Unlocks when you pick a time'], ['Your own rebel site, free forever', 'Unlocks when you pick a time']);
  return { id: `form-${band}${f.pricing ? '-pricing' : ''}${carrot ? '-ai' : ''}`, ctx: 'default', tag, headline, line, rows: [], form: f, locked,
    button: 'Keep going',
    note: carrot ? 'Finish, pick a time, and your rebel Ai account unlocks.' : 'Free and built for you. No obligation.',
    secondary: 'Leave anyway', href: '#' };
}
